import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib_app/api";
import { User, Lock, Eye, EyeOff } from "lucide-react";

const APP_VERSION = "v1.0.0";
const YEAR = new Date().getFullYear();
const PRIMARY = "#090808";
const PRIMARY_HOVER = "#000000";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [companyName, setCompanyName] = useState("Perusahaan");
  const { loginJWT } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/settings/company/public").then((r) => setCompanyName(r.data?.name || "Perusahaan")).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await loginJWT(email, password);
      toast.success("Berhasil masuk");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal masuk");
    } finally {
      setBusy(false);
    }
  };

  const forgot = (e) => {
    e.preventDefault();
    toast.info("Fitur lupa password segera hadir. Silakan hubungi administrator.");
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Vibrant blue glow layers */}
      <div className="absolute -top-40 -left-40 w-[640px] h-[640px] rounded-full bg-[#6B8FE2]/25 blur-[110px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[640px] h-[640px] rounded-full bg-[#97ADD4]/28 blur-[110px] pointer-events-none" />
      <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] rounded-full bg-[#B9C5D8]/25 blur-[80px] pointer-events-none" />

      {/* Centered stack: logo → headline → card → version */}
      <div className="min-h-screen flex items-center justify-center px-6 py-10 relative z-10">
        <div className="w-full max-w-5xl flex flex-col items-center">
          {/* Brand */}
          <img
            src="/supersales-logo.png"
            alt="Super Sales"
            data-testid="login-brand"
            className="h-8 md:h-9 w-auto select-none drop-shadow-[0_6px_16px_rgba(30,64,175,0.20)]"
            draggable={false}
          />

          {/* Headline — single line on desktop */}
          <h1
            data-testid="login-headline"
            className="mt-6 text-[24px] sm:text-[32px] md:text-4xl lg:text-[44px] xl:text-5xl font-black tracking-tight text-center text-[#090808] leading-[1.05] whitespace-normal lg:whitespace-nowrap"
            style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }}
          >
            Track Better<span style={{ color: PRIMARY }}>.</span> Follow Up Faster<span style={{ color: PRIMARY }}>.</span> Close More<span style={{ color: PRIMARY }}>.</span>
          </h1>

        {/* Login card */}
        <div
          data-testid="login-card"
          className="mt-10 w-full max-w-md rounded-3xl p-8 md:p-9"
          style={{
            background:
              "radial-gradient(500px 350px at 100% 0%, rgba(46,91,255,0.14) 0%, transparent 60%)," +
              "radial-gradient(400px 300px at 0% 100%, rgba(151,173,212,0.22) 0%, transparent 60%)," +
              "linear-gradient(135deg, rgba(255,255,255,0.85) 0%, rgba(232,240,254,0.72) 100%)",
            backdropFilter: "blur(28px) saturate(180%)",
            WebkitBackdropFilter: "blur(28px) saturate(180%)",
            border: "1px solid rgba(255,255,255,0.75)",
            boxShadow: "0 30px 60px -20px rgba(30,64,175,0.28), 0 8px 24px -12px rgba(20,22,31,0.10)",
          }}
        >
          {/* Lock icon + heading */}
          <div className="flex flex-col items-center text-center mb-6">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center mb-3"
              style={{ background: "rgba(9,8,8,0.06)", border: "1px solid rgba(9,8,8,0.10)" }}
            >
              <Lock className="w-6 h-6 text-[#090808]" strokeWidth={2} />
            </div>
            <h2 className="text-xl md:text-2xl font-bold tracking-tight text-[#090808]">Masuk ke Akun Anda</h2>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold text-[#090808]">Email atau Username</Label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A0A3A8]" strokeWidth={1.75} />
                <Input
                  id="email"
                  type="text"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Masukkan email atau username"
                  className="pl-10 h-12 rounded-xl bg-white/70"
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold text-[#090808]">Password</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A0A3A8]" strokeWidth={1.75} />
                <Input
                  id="password"
                  type={showPw ? "text" : "password"}
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Masukkan password"
                  className="pl-10 pr-11 h-12 rounded-xl bg-white/70"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  data-testid="toggle-password-visibility"
                  aria-label={showPw ? "Sembunyikan password" : "Tampilkan password"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 inline-flex items-center justify-center rounded-lg text-[#A0A3A8] hover:text-[#090808] hover:bg-black/5 transition-colors"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Remember + Forgot */}
            <div className="flex items-center justify-between pt-1">
              <label className="inline-flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-label">
                <input
                  type="checkbox"
                  data-testid="remember-me-checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="w-4 h-4 rounded border-[#A0A3A8] cursor-pointer"
                  style={{ accentColor: PRIMARY }}
                />
                <span className="text-sm text-[#626061]">Ingat saya</span>
              </label>
              <a
                href="#"
                onClick={forgot}
                data-testid="forgot-password-link"
                className="text-sm font-semibold text-[#090808] hover:underline"
              >
                Lupa password?
              </a>
            </div>

            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full h-12 rounded-full text-sm font-bold tracking-widest mt-2 text-white bg-[#090808] hover:bg-[#000000]"
              style={{
                boxShadow: "0 10px 24px rgba(9,8,8,0.28)",
              }}
            >
              {busy ? "MEMPROSES..." : "MASUK"}
            </Button>
          </form>

          <p className="text-sm text-[#626061] text-center mt-6" data-testid="admin-contact">
            Belum memiliki akses? Hubungi{" "}
            <span className="font-semibold text-[#090808]">Administrator.</span>
          </p>
        </div>

        {/* Version + Copyright stacked below card — same gap as headline→card (mt-10) */}
        <div className="mt-10 flex flex-col items-center gap-1 text-center" data-testid="login-footer">
          <div className="text-[11px] text-[#626061] font-num font-semibold tracking-wide" data-testid="app-version">
            Version {APP_VERSION}
          </div>
          <div className="text-[11px] text-[#626061]" data-testid="copyright">
            © {YEAR} <span className="font-semibold text-[#090808]">{companyName}</span>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
